import { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Full-screen lightbox with pinch-zoom + swipe navigation.
 * Props:
 *   images: string[]
 *   startIndex: number
 *   onClose: () => void
 */
export default function ImageLightbox({ images = [], startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const touchState = useRef({ startX: null, startY: null, startDist: null, startZoom: 1 });

  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [idx]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + images.length) % images.length);
      else if (e.key === "ArrowRight") setIdx((i) => (i + 1) % images.length);
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(5, z + 0.5));
      else if (e.key === "-") setZoom((z) => Math.max(1, z - 0.5));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images.length, onClose]);

  const dist = (t) => {
    const dx = t[0].clientX - t[1].clientX;
    const dy = t[0].clientY - t[1].clientY;
    return Math.hypot(dx, dy);
  };
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      touchState.current.startDist = dist(e.touches);
      touchState.current.startZoom = zoom;
    } else if (e.touches.length === 1) {
      touchState.current.startX = e.touches[0].clientX;
      touchState.current.startY = e.touches[0].clientY;
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && touchState.current.startDist) {
      const d = dist(e.touches);
      const scale = d / touchState.current.startDist;
      setZoom(Math.max(1, Math.min(5, touchState.current.startZoom * scale)));
      e.preventDefault();
    } else if (e.touches.length === 1 && zoom > 1 && touchState.current.startX != null) {
      const dx = e.touches[0].clientX - touchState.current.startX;
      const dy = e.touches[0].clientY - touchState.current.startY;
      setPan((p) => ({ x: p.x + dx * 0.3, y: p.y + dy * 0.3 }));
      touchState.current.startX = e.touches[0].clientX;
      touchState.current.startY = e.touches[0].clientY;
    }
  };
  const onTouchEnd = (e) => {
    // Swipe navigation only when NOT zoomed
    if (zoom === 1 && e.changedTouches[0] && touchState.current.startX != null) {
      const dx = e.changedTouches[0].clientX - touchState.current.startX;
      if (Math.abs(dx) > 50) {
        dx > 0 ? setIdx((i) => (i - 1 + images.length) % images.length)
              : setIdx((i) => (i + 1) % images.length);
      }
    }
    touchState.current = { startX: null, startY: null, startDist: null, startZoom: zoom };
  };

  const onWheel = (e) => {
    if (e.deltaY < 0) setZoom((z) => Math.min(5, z + 0.25));
    else setZoom((z) => Math.max(1, z - 0.25));
  };

  if (!images.length) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black flex items-center justify-center overflow-hidden touch-none"
      data-testid="lightbox"
      onWheel={onWheel}
    >
      <button
        onClick={onClose}
        data-testid="lightbox-close"
        className="absolute top-4 right-4 z-10 border-2 border-[#D4FF00] bg-black text-[#D4FF00] p-2 hover-nudge"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="absolute top-4 left-4 z-10 flex gap-2">
        <button onClick={() => setZoom((z) => Math.max(1, z - 0.5))} data-testid="lightbox-zoom-out" className="border-2 border-[#D4FF00] bg-black text-[#D4FF00] p-2 hover-nudge"><ZoomOut className="w-4 h-4" /></button>
        <button onClick={() => setZoom((z) => Math.min(5, z + 0.5))} data-testid="lightbox-zoom-in" className="border-2 border-[#D4FF00] bg-black text-[#D4FF00] p-2 hover-nudge"><ZoomIn className="w-4 h-4" /></button>
        <span className="border-2 border-[#D4FF00] bg-black text-[#D4FF00] px-3 py-2 font-mono-b text-xs font-bold uppercase">{Math.round(zoom * 100)}%</span>
      </div>
      {images.length > 1 && (
        <>
          <button
            onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
            data-testid="lightbox-prev"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 border-2 border-[#D4FF00] bg-black text-[#D4FF00] p-2 hover-nudge"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={() => setIdx((i) => (i + 1) % images.length)}
            data-testid="lightbox-next"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 border-2 border-[#D4FF00] bg-black text-[#D4FF00] p-2 hover-nudge"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 border-2 border-[#D4FF00] bg-black text-[#D4FF00] px-3 py-1 font-mono-b text-xs font-bold">
            {idx + 1} / {images.length}
          </div>
        </>
      )}
      <div
        className="w-full h-full flex items-center justify-center select-none"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={images[idx]}
          alt=""
          draggable={false}
          className="max-w-full max-h-full object-contain transition-transform duration-100"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
        />
      </div>
    </div>
  );
}
